import React, { useRef, useState } from 'react';
import { BookOpen as LibraryIcon, LogOut, MessageCircle, Settings, User, Users } from '../../bravosIcons';
import { TrainerAppLayout } from '../../shell/TrainerAppLayout';
import { CHAT_NAV_ENABLED } from '../../../../lib/chatNavEnabled';
import type { TrainerDashboardSection } from '../hooks/useTrainerDashboardNavigation';

export type TrainerDashboardLayoutProps = {
  activeSection: TrainerDashboardSection;
  onSectionSelect: (id: TrainerDashboardSection) => void;
  headerHidden: boolean;
  headerTitle: string;
  onSignOut: () => void | Promise<void>;
  membersSlot: React.ReactNode;
  chatSlot?: React.ReactNode;
  mobileSlot: React.ReactNode;
  desktopSlot: React.ReactNode;
};

export function TrainerDashboardLayout({
  activeSection,
  onSectionSelect,
  headerHidden,
  headerTitle,
  onSignOut,
  membersSlot,
  chatSlot,
  mobileSlot,
  desktopSlot,
}: TrainerDashboardLayoutProps) {
  const [isMobileProfileMenuOpen, setIsMobileProfileMenuOpen] = useState(false);
  const mobileProfileMenuRef = useRef<HTMLDivElement>(null);

  const shellBg = 'bg-page';

  return (
    <TrainerAppLayout
      activeSection={activeSection}
      onSectionSelect={onSectionSelect}
      shellClassName={shellBg}
      mainClassName="flex-1 flex flex-col overflow-hidden bg-page"
    >
        <div
          className={`bg-page border-b border-line flex-shrink-0 ${headerHidden ? 'hidden' : ''} ${activeSection === 'library' ? 'lg:hidden' : ''}`}
          style={{ width: '100%', maxWidth: '100vw', overflow: 'visible' }}
        >
          <div style={{ width: '100%', maxWidth: '100%' }} className="w-full px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center gap-2 min-w-0 min-h-10">
              <div className="flex-1 min-w-0 flex items-center min-h-10 overflow-hidden">
                <h1 className="trainer-shell-page-title text-xl text-theme-text truncate leading-tight">
                  {headerTitle}
                </h1>
              </div>
              <div
                className="relative lg:hidden flex-shrink-0"
                style={{ marginLeft: '0.5rem', zIndex: 101 }}
                ref={mobileProfileMenuRef}
              >
                <button
                  type="button"
                  onClick={() => setIsMobileProfileMenuOpen(!isMobileProfileMenuOpen)}
                  className="p-2 rounded-lg hover:bg-subtle transition-colors flex items-center justify-center"
                  aria-label="Profile menu"
                  style={{ flexShrink: 0 }}
                >
                  <User className="w-5 h-5 text-secondary" />
                </button>
                {isMobileProfileMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[100]"
                      onClick={() => setIsMobileProfileMenuOpen(false)}
                      onTouchStart={(e) => e.stopPropagation()}
                      style={{ touchAction: 'none' }}
                    />
                    <div
                      className="fixed right-4 top-16 w-48 bg-card rounded-md shadow-lg py-1 z-[101] border border-line"
                      style={{ marginTop: '0.5rem' }}
                    >
                      <button
                        type="button"
                        onClick={async () => {
                          setIsMobileProfileMenuOpen(false);
                          await onSignOut();
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsMobileProfileMenuOpen(false);
                          void onSignOut();
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-theme-text dark:text-theme-off-white hover:bg-theme-muted dark:hover:bg-white/10 flex items-center gap-2"
                        style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {activeSection === 'chat' ? (
          <div className="flex-1 min-h-0 overflow-hidden max-lg:pb-16">{chatSlot}</div>
        ) : (
        <div
          id="trainer-dashboard-main-scroll"
          className="flex-1 overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable] bg-page"
        >
          <div
            className={
              activeSection === 'members'
                ? 'w-full'
                : 'w-full px-4 sm:px-6 lg:px-8 py-8'
            }
            style={{ position: 'relative' }}
          >
            {membersSlot}
            <div
              className={`lg:hidden ${activeSection === 'members' ? '' : 'mb-20'}`}
              style={{ position: 'relative', zIndex: 1 }}
            >
              {mobileSlot}
            </div>
            <div className="hidden lg:block">{desktopSlot}</div>
          </div>
        </div>
        )}

      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-page border-t border-line z-50"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-center justify-around h-16">
          <button
            type="button"
            onClick={() => onSectionSelect('members')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              activeSection === 'members' ? 'text-theme-text' : 'text-secondary'
            }`}
          >
            <Users className={`w-6 h-6 mb-1 ${activeSection === 'members' ? 'text-theme-text' : 'text-secondary'}`} />
            <span className="text-xs font-medium">Members</span>
          </button>
          <button
            type="button"
            onClick={() => onSectionSelect('library')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              activeSection === 'library' ? 'text-theme-text' : 'text-secondary'
            }`}
          >
            <LibraryIcon
              className={`w-6 h-6 mb-1 ${
                activeSection === 'library' ? 'text-theme-text' : 'text-secondary'
              }`}
            />
            <span className="text-xs font-medium">Library</span>
          </button>
          {CHAT_NAV_ENABLED ? (
          <button
            type="button"
            onClick={() => onSectionSelect('chat')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              activeSection === 'chat' ? 'text-theme-text' : 'text-secondary'
            }`}
          >
            <MessageCircle className={`w-6 h-6 mb-1 ${activeSection === 'chat' ? 'text-theme-text' : 'text-secondary'}`} />
            <span className="text-xs font-medium">Chat</span>
          </button>
          ) : null}
          <button
            type="button"
            onClick={() => onSectionSelect('control')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              activeSection === 'control' ? 'text-theme-text' : 'text-secondary'
            }`}
          >
            <Settings className={`w-6 h-6 mb-1 ${activeSection === 'control' ? 'text-theme-text' : 'text-secondary'}`} />
            <span className="text-xs font-medium">Control</span>
          </button>
        </div>
      </div>
    </TrainerAppLayout>
  );
}
